// src/pages/staff/StaffMigrationCenter.js
import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Box, Paper, Typography, Button, Tabs, Tab, Stack, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, Card, CardContent, Divider, Chip, Stepper, Step, StepLabel
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    GetApp as DownloadIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    PlayArrow as RunIcon,
    ArrowBack as BackIcon,
    ListAlt as ListIcon
} from '@mui/icons-material';
import AppHeader from '../../components/AppHeader';
import RecurringPayrollImportWizard from './RecurringPayrollImportWizard';

const StaffMigrationCenter = ({ setIsLoggedIn }) => {
    const location = useLocation();
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    
    // Tab values: 0 = Profile Import, 1 = Workflow Mapping, ... 9 = Qualifications
    const [tabVal, setTabVal] = useState(location.state?.initialTab !== undefined ? location.state.initialTab : 0);
    const [importMode, setImportMode] = useState('standard');

    // Profile Import Wizard Steps
    const [profileStep, setProfileStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [profileFile, setProfileFile] = useState(null);
    const [profileValidation, setProfileValidation] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [loadingVal, setLoadingVal] = useState(false);

    // Workflow Mapping Steps
    const [wfStep, setWfStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [wfFile, setWfFile] = useState(null);
    const [wfResult, setWfResult] = useState(null);
    const [wfLoading, setWfLoading] = useState(false);

    // Shifts & Locations Mapping Steps
    const [slStep, setSlStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [slFile, setSlFile] = useState(null);
    const [slValidation, setSlValidation] = useState(null);
    const [slImporting, setSlImporting] = useState(false);
    const [slResult, setSlResult] = useState(null);
    const [slLoading, setSlLoading] = useState(false);

    // Contact Details Steps
    const [cdStep, setCdStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [cdFile, setCdFile] = useState(null);
    const [cdValidation, setCdValidation] = useState(null);
    const [cdImporting, setCdImporting] = useState(false);
    const [cdResult, setCdResult] = useState(null);
    const [cdLoadingVal, setCdLoadingVal] = useState(false);

    // Discipline Records Steps
    const [dispStep, setDispStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [dispFile, setDispFile] = useState(null);
    const [dispValidation, setDispValidation] = useState(null);
    const [dispImporting, setDispImporting] = useState(false);
    const [dispResult, setDispResult] = useState(null);
    const [dispLoadingVal, setDispLoadingVal] = useState(false);

    // Document Records Steps
    const [docStep, setDocStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [docFile, setDocFile] = useState(null);
    const [docValidation, setDocValidation] = useState(null);
    const [docImporting, setDocImporting] = useState(false);
    const [docResult, setDocResult] = useState(null);
    const [docLoadingVal, setDocLoadingVal] = useState(false);

    // Family Records Steps
    const [famStep, setFamStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [famFile, setFamFile] = useState(null);
    const [famValidation, setFamValidation] = useState(null);
    const [famImporting, setFamImporting] = useState(false);
    const [famResult, setFamResult] = useState(null);
    const [famLoadingVal, setFamLoadingVal] = useState(false);

    // Historical Leave Records Steps
    const [leaveStep, setLeaveStep] = useState(0);
    const [leaveFile, setLeaveFile] = useState(null);
    const [leaveValidation, setLeaveValidation] = useState(null);
    const [leaveImporting, setLeaveImporting] = useState(false);
    const [leaveResult, setLeaveResult] = useState(null);
    const [leaveLoadingVal, setLeaveLoadingVal] = useState(false);

    // Qualifications Wizard Steps
    const [qualStep, setQualStep] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [qualFile, setQualFile] = useState(null);
    const [qualValidation, setQualValidation] = useState(null);
    const [qualImporting, setQualImporting] = useState(false);
    const [qualResult, setQualResult] = useState(null);
    const [qualLoadingVal, setQualLoadingVal] = useState(false);

    // Fetch Token
    const token = sessionStorage.getItem("token") || "";

    // 1. Download Templates
    const handleDownloadTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleDownloadWorkflowTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/workflows/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleDownloadSLTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/shifts-locations/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleDownloadContactDetailsTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/contact-details/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleDownloadDispTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/discipline/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    // 2. Handle Profile Validation Upload
    const handleProfileFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setProfileFile(file);
        setLoadingVal(true);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('importMode', importMode);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/validate`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setProfileValidation(res.data);
            setProfileStep(1); // Move to review step
        } catch (err) {
            console.error(err);
            alert("Excel validation failed: " + (err.response?.data?.message || err.message));
        } finally {
            setLoadingVal(false);
        }
    };

    // 3. Execute Profiles Import
    const handleExecuteProfileImport = async () => {
        if (!profileValidation || profileValidation.validRows === 0) return;
        setImporting(true);

        // Filter only valid records to send to backend import execution
        const validRecords = profileValidation.records
            .filter(r => r.isValid)
            .map(r => r.data);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/execute`,
                { records: validRecords, importMode },
                {
                    withCredentials: true,
                    headers: { 'X-Tenant': tenant }
                }
            );
            setImportResult(res.data);
            setProfileStep(2); // Success step
        } catch (err) {
            console.error(err);
            alert("Migration failed: " + (err.response?.data?.message || err.message));
        } finally {
            setImporting(false);
        }
    };

    // 4. Handle Workflow Upload & Ingestion
    const handleWfFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setWfFile(file);
        setWfLoading(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/workflows`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setWfResult(res.data);
            setWfStep(1); // Success step
        } catch (err) {
            console.error(err);
            alert("Workflow Mapping failed: " + (err.response?.data?.message || err.message));
        } finally {
            setWfLoading(false);
        }
    };

    // 5. Handle Shifts & Locations Upload & Validation
    const handleSlFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setSlFile(file);
        setSlLoading(true);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('dryRun', 'true');

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/shifts-locations`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setSlValidation(res.data.validation);
            setSlStep(1); // Move to validation review step
        } catch (err) {
            console.error(err);
            alert("Validation failed: " + (err.response?.data?.message || err.message));
            setSlFile(null);
        } finally {
            setSlLoading(false);
            e.target.value = ''; // Reset input
        }
    };

    const handleExecuteSlImport = async () => {
        if (!slFile) return;
        setSlImporting(true);
        const formData = new FormData();
        formData.append('file', slFile);
        formData.append('ignoreErrors', 'true'); // We want to force import valid records

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/shifts-locations`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setSlResult(res.data);
            setSlStep(2); // Success step
        } catch (err) {
            console.error(err);
            alert("Shifts & Locations Import failed: " + (err.response?.data?.message || err.message));
        } finally {
            setSlImporting(false);
        }
    };

    // 6. Handle Contact Details Validation Upload
    const handleCdFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setCdFile(file);
        setCdLoadingVal(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/contact-details/validate`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setCdValidation(res.data);
            setCdStep(1); // Move to review step
        } catch (err) {
            console.error(err);
            alert("Excel validation failed: " + (err.response?.data?.message || err.message || err.response?.data?.error));
        } finally {
            setCdLoadingVal(false);
        }
    };

    // 7. Execute Contact Details Import
    const handleExecuteCdImport = async () => {
        if (!cdValidation || cdValidation.summary.valid_count === 0) return;
        setCdImporting(true);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/contact-details/execute`,
                { records: cdValidation.valid_records },
                {
                    withCredentials: true,
                    headers: { 'X-Tenant': tenant }
                }
            );
            setCdResult(res.data);
            setCdStep(2); // Success step
        } catch (err) {
            console.error(err);
            alert("Migration failed: " + (err.response?.data?.message || err.message || err.response?.data?.error));
        } finally {
            setCdImporting(false);
        }
    };

    const resetProfilesWizard = () => {
        setProfileStep(0);
        setProfileFile(null);
        setProfileValidation(null);
        setImportResult(null);
    };

    const resetWfWizard = () => {
        setWfStep(0);
        setWfFile(null);
        setWfResult(null);
    };

    const resetSlWizard = () => {
        setSlStep(0);
        setSlFile(null);
        setSlValidation(null);
        setSlResult(null);
    };

    const resetCdWizard = () => {
        setCdStep(0);
        setCdFile(null);
        setCdValidation(null);
        setCdResult(null);
    };

    const handleDispFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setDispFile(file);
        setDispLoadingVal(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/discipline/validate`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setDispValidation(res.data);
            setDispStep(1); // Move to review step
        } catch (err) {
            console.error(err);
            alert("Excel validation failed: " + (err.response?.data?.message || err.message));
        } finally {
            setDispLoadingVal(false);
        }
    };

    const handleExecuteDispImport = async () => {
        if (!dispValidation || dispValidation.validRows === 0) return;
        setDispImporting(true);

        const validRecords = dispValidation.records
            .filter(r => r.isValid)
            .map(r => r.data);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/discipline/execute`,
                { records: validRecords },
                {
                    withCredentials: true,
                    headers: { 'X-Tenant': tenant }
                }
            );
            setDispResult(res.data);
            setDispStep(2); // Success step
        } catch (err) {
            console.error(err);
            alert("Migration failed: " + (err.response?.data?.message || err.message));
        } finally {
            setDispImporting(false);
        }
    };

    const resetDispWizard = () => {
        setDispStep(0);
        setDispFile(null);
        setDispValidation(null);
        setDispResult(null);
    };

    const handleDownloadDocTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/documents/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleDocFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setDocFile(file);
        setDocLoadingVal(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/documents/validate`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setDocValidation(res.data);
            setDocStep(1); // Move to review step
        } catch (err) {
            console.error(err);
            alert("Excel validation failed: " + (err.response?.data?.message || err.message));
        } finally {
            setDocLoadingVal(false);
        }
    };

    const handleExecuteDocImport = async () => {
        if (!docValidation || docValidation.validRows === 0) return;
        setDocImporting(true);

        const validRecords = docValidation.records
            .filter(r => r.isValid)
            .map(r => r.data);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/documents/execute`,
                { records: validRecords },
                {
                    withCredentials: true,
                    headers: { 'X-Tenant': tenant }
                }
            );
            setDocResult(res.data);
            setDocStep(2); // Success step
        } catch (err) {
            console.error(err);
            alert("Migration failed: " + (err.response?.data?.message || err.message));
        } finally {
            setDocImporting(false);
        }
    };

    const resetDocWizard = () => {
        setDocStep(0);
        setDocFile(null);
        setDocValidation(null);
        setDocResult(null);
    };

    const handleDownloadFamTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/family/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleFamFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFamFile(file);
        setFamLoadingVal(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/family/validate`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setFamValidation(res.data);
            setFamStep(1); // Move to review step
        } catch (err) {
            console.error(err);
            alert("Excel validation failed: " + (err.response?.data?.message || err.message));
        } finally {
            setFamLoadingVal(false);
        }
    };

    const handleExecuteFamImport = async () => {
        if (!famValidation || famValidation.validRows === 0) return;
        setFamImporting(true);

        const validRecords = famValidation.records
            .filter(r => r.isValid)
            .map(r => r.data);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/family/execute`,
                { records: validRecords },
                {
                    withCredentials: true,
                    headers: { 'X-Tenant': tenant }
                }
            );
            setFamResult(res.data);
            setFamStep(2); // Success step
        } catch (err) {
            console.error(err);
            alert("Migration failed: " + (err.response?.data?.message || err.message));
        } finally {
            setFamImporting(false);
        }
    };

    const resetFamWizard = () => {
        setFamStep(0);
        setFamFile(null);
        setFamValidation(null);
        setFamResult(null);
    };

    const handleDownloadLeaveTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/leave-history/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleLeaveFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLeaveFile(file);
        setLeaveLoadingVal(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/leave-history/validate`,
                formData,
                { withCredentials: true, headers: { 'X-Tenant': tenant, 'Content-Type': 'multipart/form-data' } }
            );
            setLeaveValidation(res.data);
            setLeaveStep(1);
        } catch (err) {
            console.error(err);
            alert("Leave history validation failed: " + (err.response?.data?.message || err.message));
            setLeaveFile(null);
        } finally {
            setLeaveLoadingVal(false);
            e.target.value = '';
        }
    };

    const handleExecuteLeaveImport = async () => {
        if (!leaveValidation || leaveValidation.validRows === 0) return;
        setLeaveImporting(true);
        const records = leaveValidation.records.filter(record => record.isValid).map(record => record.data);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/leave-history/execute`,
                { records, filename: leaveValidation.filename || leaveFile?.name },
                { withCredentials: true, headers: { 'X-Tenant': tenant } }
            );
            setLeaveResult(res.data);
            setLeaveStep(2);
        } catch (err) {
            console.error(err);
            alert("Leave history import failed: " + (err.response?.data?.message || err.message));
        } finally {
            setLeaveImporting(false);
        }
    };

    const resetLeaveWizard = () => {
        setLeaveStep(0);
        setLeaveFile(null);
        setLeaveValidation(null);
        setLeaveResult(null);
    };

    // Qualifications Handlers
    const handleDownloadQualTemplate = () => {
        window.open(`${process.env.REACT_APP_STAFF_API_URL}/staff-import/qualifications/template?tenant=${tenant}&token=${token}`, '_blank');
    };

    const handleQualFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setQualFile(file);
        setQualLoadingVal(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/qualifications/validate`,
                formData,
                {
                    withCredentials: true,
                    headers: {
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setQualValidation(res.data);
            setQualStep(1);
        } catch (err) {
            console.error(err);
            alert("Qualification validation failed: " + (err.response?.data?.message || err.message));
        } finally {
            setQualLoadingVal(false);
        }
    };

    const handleExecuteQualImport = async () => {
        if (!qualValidation || qualValidation.validRows === 0) return;
        setQualImporting(true);

        const rows = qualValidation.records.filter(r => r.isValid);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_STAFF_API_URL}/staff-import/qualifications/execute`,
                { rows },
                {
                    withCredentials: true,
                    headers: { 'X-Tenant': tenant }
                }
            );
            setQualResult(res.data);
            setQualStep(2);
        } catch (err) {
            console.error(err);
            alert("Qualifications migration failed: " + (err.response?.data?.message || err.message));
        } finally {
            setQualImporting(false);
        }
    };

    const resetQualWizard = () => {
        setQualStep(0);
        setQualFile(null);
        setQualValidation(null);
        setQualResult(null);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Header Banner */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', mb: 1 }}>
                            Staff Migration Center
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Seamlessly import organizational staff records and workflow configurations directly from Excel.
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        sx={{
                            color: 'primary.main',
                            borderColor: 'primary.main',
                            '&:hover': { borderColor: 'primary.dark', bgcolor: '#f0f0ff' },
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none'
                        }}
                    >
                        Download Migration Template
                    </Button>
                </Box>

                {/* Main Tabs */}
                <Paper sx={{ borderRadius: 5, overflow: 'hidden', mb: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 25px 0 rgba(0,0,0,0.02)' }}>
                    <Tabs
                        value={tabVal}
                        onChange={(e, v) => setTabVal(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            borderBottom: '1px solid #e2e8f0',
                            bgcolor: '#fdfdfd',
                            '& .MuiTabs-indicator': { bgcolor: 'primary.main', height: 3 }
                        }}
                    >
                        <Tab label="Step 1: Profiles Import" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 2: Workflow Mappings" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 3: Shifts & Locations" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 4: Contact Details" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 5: Discipline Records" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 6: Document Records" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 7: Family Records" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 8: Leave History" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 9: Recurring Payroll Elements" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                        <Tab label="Step 10: Qualifications" sx={{ fontWeight: 800, textTransform: 'none', px: 4, py: 2, '&.Mui-selected': { color: 'primary.main' } }} />
                    </Tabs>

                    {/* Step 1: Staff Profiles Wizard */}
                    {tabVal === 0 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={profileStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Profile Spreadsheet</StepLabel></Step>
                                <Step><StepLabel>Validation & Dry Run</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {/* Sub-step 0: File Upload */}
                            {profileStep === 0 && (
                                <Box sx={{ py: 4, maxWidth: 650, mx: 'auto' }}>
                                    {loadingVal ? (
                                        <Box sx={{ textAlign: 'center', py: 6 }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Analyzing & validating spreadsheet rows...</Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            {/* Import Mode Selector */}
                                            <Typography variant="body1" sx={{ fontWeight: 800, color: '#0f172a', mb: 2, textAlign: 'left' }}>
                                                Select Onboarding Validation Mode:
                                            </Typography>
                                            <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                                <Card 
                                                    onClick={() => setImportMode('standard')}
                                                    sx={{ 
                                                        flex: 1, 
                                                        cursor: 'pointer',
                                                        borderRadius: 4,
                                                        border: importMode === 'standard' ? '2px solid #6366f1' : '1px solid #e2e8f0',
                                                        boxShadow: importMode === 'standard' ? (theme) => `0 10px 25px -5px ${theme.palette.primary.main}33` : 'none',
                                                        bgcolor: importMode === 'standard' ? '#f5f3ff' : '#ffffff',
                                                        transition: 'all 0.2s',
                                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px -5px rgba(0,0,0,0.04)' }
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 3, textAlign: 'left' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: importMode === 'standard' ? 'primary.main' : '#1e293b', mb: 1 }}>
                                                            Standard Import
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                                            Strict verification. Requires NRC, email/username, system role, and details for complete employee files.
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                                <Card 
                                                    onClick={() => setImportMode('lightweight')}
                                                    sx={{ 
                                                        flex: 1, 
                                                        cursor: 'pointer',
                                                        borderRadius: 4,
                                                        border: importMode === 'lightweight' ? '2px solid #6366f1' : '1px solid #e2e8f0',
                                                        boxShadow: importMode === 'lightweight' ? (theme) => `0 10px 25px -5px ${theme.palette.primary.main}33` : 'none',
                                                        bgcolor: importMode === 'lightweight' ? '#f5f3ff' : '#ffffff',
                                                        transition: 'all 0.2s',
                                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px -5px rgba(0,0,0,0.04)' }
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 3, textAlign: 'left' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: importMode === 'lightweight' ? 'primary.main' : '#1e293b', mb: 1 }}>
                                                            Lightweight Roster Import
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                                            Minimal verification. NRC and email/username are optional. Generates username from Code & password "123456".
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Stack>

                                            <Box
                                                component="label"
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px dashed #cbd5e1',
                                                    borderRadius: 5,
                                                    p: 6,
                                                    cursor: 'pointer',
                                                    bgcolor: '#f8fafc',
                                                    transition: 'all 0.2s',
                                                    '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                                }}
                                            >
                                                <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                                                    Drag & Drop Staff Excel Sheet
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                                                    Supports .xlsx up to 500 records
                                                </Typography>
                                                <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                    Browse Local Files
                                                </Button>
                                                <input type="file" hidden accept=".xlsx" onChange={handleProfileFileChange} />
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Sub-step 1: Review Validation Grid */}
                            {profileStep === 1 && profileValidation && (
                                <Box>
                                    {/* Stats Banner */}
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{profileValidation.validRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{profileValidation.invalidRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Rows with Errors (Will be Skipped)</Typography>
                                            </CardContent>
                                        </Card>
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetProfilesWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Back to Upload
                                        </Button>
                                        <Button
                                            startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                            onClick={handleExecuteProfileImport}
                                            variant="contained"
                                            disabled={importing || profileValidation.validRows === 0}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                                        >
                                            {importing ? "Executing Migration..." : `Import ${profileValidation.validRows} Valid Records`}
                                        </Button>
                                    </Box>

                                    {/* Table Preview Grid */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Excel Row</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Employee Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Full Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Work Email</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>NRC Number</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Department / Branch</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Validation Errors</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {profileValidation.records.map((row) => (
                                                    <TableRow key={row.rowNumber} sx={{ bgcolor: row.isValid ? 'inherit' : '#fff5f5' }}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.rowNumber}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={row.isValid ? "Ready" : "Invalid"}
                                                                color={row.isValid ? "success" : "error"}
                                                                size="small"
                                                                sx={{ fontWeight: 800 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.data.staff_code || '-'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>
                                                            {row.data.salutation ? `${row.data.salutation} ` : ''}{row.data.full_name || '-'}
                                                        </TableCell>
                                                        <TableCell>{row.data.email || '-'}</TableCell>
                                                        <TableCell>{row.data.nrc_number || '-'}</TableCell>
                                                        <TableCell>{row.data.department || '-'} / {row.data.branch_name || '-'}</TableCell>
                                                        <TableCell sx={{ color: '#b91c1c', fontWeight: 600 }}>
                                                            {row.errors.length > 0 ? (
                                                                <Stack spacing={0.5}>
                                                                    {row.errors.map((err, idx) => (
                                                                        <Typography key={idx} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <ErrorIcon sx={{ fontSize: 14 }} /> {err}
                                                                        </Typography>
                                                                    ))}
                                                                </Stack>
                                                            ) : (
                                                                <Typography variant="caption" sx={{ color: '#047857', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <SuccessIcon sx={{ fontSize: 14 }} /> Pass
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* Sub-step 2: Success summary */}
                            {profileStep === 2 && importResult && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Migration Completed Successfully!</Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {importResult.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>Onboarding Summary</Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Records Processed:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{importResult.importedCount}</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Default Credentials Hashed:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>Yes (Last 6 Digits of NRC)</Typography>
                                        </Stack>
                                    </Box>
                                    <Stack direction="row" spacing={2} justifyContent="center">
                                        <Button variant="outlined" onClick={resetProfilesWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Import Another File
                                        </Button>
                                        <Button variant="contained" onClick={() => setTabVal(1)} sx={{ bgcolor: 'primary.main', borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Proceed to Step 2: Workflows
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 2: Workflow Mapping Wizard */}
                    {tabVal === 1 && (
                        <Box sx={{ p: 4 }}>
                            {wfStep === 0 ? (
                                <Box sx={{ py: 4, maxWidth: 600, mx: 'auto' }}>
                                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Workflow Mapping Instructions</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                                            Ensure all staff members are imported first (Step 1). The Workflow spreadsheet must contain Employee Code, Manager Employee Code, and HR Employee Code.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadWorkflowTemplate}
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Download Pre-filled Workflow Template
                                        </Button>
                                    </Alert>

                                    {wfLoading ? (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Mapping approval relationships...</Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: 5,
                                                p: 6,
                                                cursor: 'pointer',
                                                bgcolor: '#f8fafc',
                                                transition: 'all 0.2s',
                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Workflow Spreadsheet</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Standard relationship file mapping (EMP001 -&gt; EMP002)</Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                Select Workflow Excel File
                                            </Button>
                                            <input type="file" hidden accept=".xlsx" onChange={handleWfFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Workflows Assigned Successfully!</Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {wfResult?.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>Workflow Statistics</Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Workflow assignments updated:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{wfResult?.mappedCount}</Typography>
                                        </Stack>
                                    </Box>
                                    <Button variant="outlined" onClick={resetWfWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                        Upload Another Workflow File
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 3: Shifts & Locations Mapping Wizard */}
                    {tabVal === 2 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={slStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Shifts & Locations Spreadsheet</StepLabel></Step>
                                <Step><StepLabel>Validation & Dry Run</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {slStep === 0 && (
                                <Box sx={{ py: 4, maxWidth: 600, mx: 'auto' }}>
                                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Shifts & Locations Mapping Instructions</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                                            Ensure all staff members are imported first (Step 1). The Shifts & Locations spreadsheet must contain Employee Code, Shift Code, and a comma-separated list of Location Codes.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadSLTemplate}
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Download Pre-filled Shifts & Locations Template
                                        </Button>
                                    </Alert>

                                    {slLoading ? (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Mapping shifts and locations...</Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: 5,
                                                p: 6,
                                                cursor: 'pointer',
                                                bgcolor: '#f8fafc',
                                                transition: 'all 0.2s',
                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Shifts & Locations Spreadsheet</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Standard file mapping (EMP001 -&gt; SHIFT01, LOC1)</Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                Select Shifts & Locations Excel File
                                            </Button>
                                            <input type="file" hidden accept=".xlsx" onChange={handleSlFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Sub-step 1: Review Validation Grid */}
                            {slStep === 1 && slValidation && (
                                <Box>
                                    {/* Stats Banner */}
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{slValidation.valid_count}</Typography>
                                                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Update</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{slValidation.error_count}</Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Rows with Errors (Will be Skipped)</Typography>
                                            </CardContent>
                                        </Card>
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetSlWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Back to Upload
                                        </Button>
                                        <Button
                                            startIcon={slImporting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                            onClick={handleExecuteSlImport}
                                            variant="contained"
                                            disabled={slImporting || slValidation.valid_count === 0}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                                        >
                                            {slImporting ? "Executing Update..." : `Update ${slValidation.valid_count} Valid Records`}
                                        </Button>
                                    </Box>

                                    {/* Table Preview Grid */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Excel Row</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Employee Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Validation Errors</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {slValidation.valid_records.map((row, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.row}</TableCell>
                                                        <TableCell>
                                                            <Chip label="Ready" color="success" size="small" sx={{ fontWeight: 800 }} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.staff_code}</TableCell>
                                                        <TableCell sx={{ color: '#047857', fontWeight: 600 }}>
                                                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <SuccessIcon sx={{ fontSize: 14 }} /> Pass
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {slValidation.errors.map((row, idx) => (
                                                    <TableRow key={`err-${idx}`} sx={{ bgcolor: '#fff5f5' }}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.row}</TableCell>
                                                        <TableCell>
                                                            <Chip label="Invalid" color="error" size="small" sx={{ fontWeight: 800 }} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.staff_code || '-'}</TableCell>
                                                        <TableCell sx={{ color: '#b91c1c', fontWeight: 600 }}>
                                                            <Stack spacing={0.5}>
                                                                {row.errors.map((err, i) => (
                                                                    <Typography key={i} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <ErrorIcon sx={{ fontSize: 14 }} /> {err}
                                                                    </Typography>
                                                                ))}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {slStep === 2 && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    {slResult?.success === false ? (
                                        <>
                                            <ErrorIcon sx={{ fontSize: 80, color: '#ef4444', mb: 3 }} />
                                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Import Failed</Typography>
                                        </>
                                    ) : (
                                        <>
                                            <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Shifts & Locations Assigned Successfully!</Typography>
                                        </>
                                    )}
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {slResult?.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>Mapping Statistics</Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Shift & Location assignments updated:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{slResult?.mappedCount || 0}</Typography>
                                        </Stack>
                                    </Box>

                                    <Button variant="outlined" onClick={resetSlWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                        Upload Another File
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* Step 4: Contact Details Update Wizard */}
                    {tabVal === 3 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={cdStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Contact Details Spreadsheet</StepLabel></Step>
                                <Step><StepLabel>Validation & Dry Run</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {/* Sub-step 0: File Upload */}
                            {cdStep === 0 && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Contact Details Import Instructions</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                                            Use this to update bulk contact details for existing staff members.
                                            You must provide the Employee Code (staff_code).
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadContactDetailsTemplate}
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Download Contact Details Template
                                        </Button>
                                    </Alert>

                                    {cdLoadingVal ? (
                                        <Box sx={{ py: 4 }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Analyzing & validating spreadsheet rows...</Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: 5,
                                                p: 6,
                                                cursor: 'pointer',
                                                bgcolor: '#f8fafc',
                                                transition: 'all 0.2s',
                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Contact Details Excel Sheet</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Supports .xlsx up to 500 records</Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                Browse Local Files
                                            </Button>
                                            <input type="file" hidden accept=".xlsx" onChange={handleCdFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Sub-step 1: Review Validation Grid */}
                            {cdStep === 1 && cdValidation && (
                                <Box>
                                    {/* Stats Banner */}
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{cdValidation.summary.valid_count}</Typography>
                                                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Update</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{cdValidation.summary.error_count}</Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Rows with Errors (Will be Skipped)</Typography>
                                            </CardContent>
                                        </Card>
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetCdWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Back to Upload
                                        </Button>
                                        <Button
                                            startIcon={cdImporting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                            onClick={handleExecuteCdImport}
                                            variant="contained"
                                            disabled={cdImporting || cdValidation.summary.valid_count === 0}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                                        >
                                            {cdImporting ? "Executing Update..." : `Update ${cdValidation.summary.valid_count} Valid Records`}
                                        </Button>
                                    </Box>

                                    {/* Table Preview Grid */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Excel Row</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Employee Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Phone</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Email</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Bank Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Bank Account No</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Validation Errors</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {cdValidation.valid_records.map((row, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.row}</TableCell>
                                                        <TableCell>
                                                            <Chip label="Ready" color="success" size="small" sx={{ fontWeight: 800 }} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.staff_code}</TableCell>
                                                        <TableCell>{row.phone || '-'}</TableCell>
                                                        <TableCell>{row.email || '-'}</TableCell>
                                                        <TableCell>{row.bank_name || '-'}</TableCell>
                                                        <TableCell>{row.bank_account_no || '-'}</TableCell>
                                                        <TableCell sx={{ color: '#047857', fontWeight: 600 }}>
                                                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <SuccessIcon sx={{ fontSize: 14 }} /> Pass
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {cdValidation.errors.map((row, idx) => (
                                                    <TableRow key={`err-${idx}`} sx={{ bgcolor: '#fff5f5' }}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.row}</TableCell>
                                                        <TableCell>
                                                            <Chip label="Invalid" color="error" size="small" sx={{ fontWeight: 800 }} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.staff_code || '-'}</TableCell>
                                                        <TableCell>-</TableCell>
                                                        <TableCell>-</TableCell>
                                                        <TableCell>-</TableCell>
                                                        <TableCell>-</TableCell>
                                                        <TableCell sx={{ color: '#b91c1c', fontWeight: 600 }}>
                                                            <Stack spacing={0.5}>
                                                                {row.errors.map((err, i) => (
                                                                    <Typography key={i} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <ErrorIcon sx={{ fontSize: 14 }} /> {err}
                                                                    </Typography>
                                                                ))}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* Sub-step 2: Success summary */}
                            {cdStep === 2 && cdResult && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Update Completed Successfully!</Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {cdResult.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>Update Statistics</Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Staff records updated:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{cdResult.updatedCount}</Typography>
                                        </Stack>
                                    </Box>
                                    <Button variant="outlined" onClick={resetCdWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                        Update More Records
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                    {/* Step 5: Discipline Records Wizard */}
                    {tabVal === 4 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={dispStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Discipline Spreadsheet</StepLabel></Step>
                                <Step><StepLabel>Validation & Dry Run</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {/* Sub-step 0: File Upload */}
                            {dispStep === 0 && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Discipline Records Import Instructions</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                                            Use this to import disciplinary records in bulk. You must provide the Employee ID / Number (or Staff Code).
                                            The system automatically maps columns dynamically and supports old database ID translations.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadDispTemplate}
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Download Discipline Template
                                        </Button>
                                    </Alert>

                                    {dispLoadingVal ? (
                                        <Box sx={{ py: 4 }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Analyzing & validating spreadsheet rows...</Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: 5,
                                                p: 6,
                                                cursor: 'pointer',
                                                bgcolor: '#f8fafc',
                                                transition: 'all 0.2s',
                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Discipline Excel or CSV Sheet</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Supports .xlsx or .csv files</Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                Browse Local Files
                                            </Button>
                                            <input type="file" hidden accept=".xlsx,.csv" onChange={handleDispFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Sub-step 1: Review Validation Grid */}
                            {dispStep === 1 && dispValidation && (
                                <Box>
                                    {/* Stats Banner */}
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{dispValidation.validRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{dispValidation.invalidRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Rows with Errors (Will be Skipped)</Typography>
                                            </CardContent>
                                        </Card>
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetDispWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Back to Upload
                                        </Button>
                                        <Button
                                            startIcon={dispImporting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                            onClick={handleExecuteDispImport}
                                            variant="contained"
                                            disabled={dispImporting || dispValidation.validRows === 0}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                                        >
                                            {dispImporting ? "Executing Import..." : `Import ${dispValidation.validRows} Valid Records`}
                                        </Button>
                                    </Box>

                                    {/* Table Preview Grid */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Excel Row</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Employee ID / Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Staff Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Category</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Severity</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Officer</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Outcome</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Description</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Validation Errors &amp; Warnings</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {dispValidation.records.map((row) => (
                                                    <TableRow key={row.rowNumber} sx={{ bgcolor: row.isValid ? 'inherit' : '#fff5f5' }}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.rowNumber}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={row.isValid ? "Ready" : "Invalid"}
                                                                color={row.isValid ? "success" : "error"}
                                                                size="small"
                                                                sx={{ fontWeight: 800 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.data.staff_code || '-'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{row.data.staff_name || '-'}</TableCell>
                                                        <TableCell>{row.data.discipline_code || '-'}</TableCell>
                                                        <TableCell>{row.data.severe_level_code || '-'}</TableCell>
                                                        <TableCell>{row.data.case_date || '-'}</TableCell>
                                                        <TableCell>{row.data.officer_code || '-'}</TableCell>
                                                        <TableCell>{row.data.outcome_code || '-'}</TableCell>
                                                        <TableCell sx={{ maxWidth: 205, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {row.data.case_desc || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            <Stack spacing={0.5}>
                                                                {row.errors.map((err, idx) => (
                                                                    <Typography key={`err-${idx}`} variant="caption" sx={{ color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <ErrorIcon sx={{ fontSize: 14 }} /> {err}
                                                                    </Typography>
                                                                ))}
                                                                {row.warnings.map((warn, idx) => (
                                                                    <Typography key={`warn-${idx}`} variant="caption" sx={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <WarningIcon sx={{ fontSize: 14 }} /> {warn}
                                                                    </Typography>
                                                                ))}
                                                                {row.errors.length === 0 && row.warnings.length === 0 && (
                                                                    <Typography variant="caption" sx={{ color: '#047857', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <SuccessIcon sx={{ fontSize: 14 }} /> Pass
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* Sub-step 2: Success summary */}
                            {dispStep === 2 && dispResult && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Discipline Records Imported Successfully!</Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {dispResult.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>Import Statistics</Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Discipline records created:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{dispResult.importedCount}</Typography>
                                        </Stack>
                                    </Box>
                                    <Button variant="outlined" onClick={resetDispWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                        Import More Records
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 6: Document Records Wizard */}
                    {tabVal === 5 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={docStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Document Records Sheet</StepLabel></Step>
                                <Step><StepLabel>Validation & Dry Run</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {/* Sub-step 0: File Upload */}
                            {docStep === 0 && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Document Records Import Instructions</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                                            Use this step to bulk import legacy document/contract records into the Verified Document Vault.
                                            The system resolves employee codes (e.g. Employee ID or Staff Code) to active profiles and imports records as Metadata-Only.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadDocTemplate}
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Download Document Template
                                        </Button>
                                    </Alert>

                                    {docLoadingVal ? (
                                        <Box sx={{ py: 4 }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Analyzing & validating spreadsheet rows...</Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: 5,
                                                p: 6,
                                                cursor: 'pointer',
                                                bgcolor: '#f8fafc',
                                                transition: 'all 0.2s',
                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Document Excel or CSV Sheet</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Supports .xlsx or .csv files</Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                Browse Local Files
                                            </Button>
                                            <input type="file" hidden accept=".xlsx,.csv" onChange={handleDocFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Sub-step 1: Review Validation Grid */}
                            {docStep === 1 && docValidation && (
                                <Box>
                                    {/* Stats Banner */}
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{docValidation.validRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{docValidation.invalidRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Rows with Errors (Will be Skipped)</Typography>
                                            </CardContent>
                                        </Card>
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetDocWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Back to Upload
                                        </Button>
                                        <Button
                                            startIcon={docImporting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                            onClick={handleExecuteDocImport}
                                            variant="contained"
                                            disabled={docImporting || docValidation.validRows === 0}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                                        >
                                            {docImporting ? "Executing Import..." : `Import ${docValidation.validRows} Valid Records`}
                                        </Button>
                                    </Box>

                                    {/* Table Preview Grid */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Excel Row</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Employee ID / Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Staff Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Doc Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Doc No.</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Start Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>End Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Notes / Remarks</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Validation Errors &amp; Warnings</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {docValidation.records.map((row) => (
                                                    <TableRow key={row.rowNumber} sx={{ bgcolor: row.isValid ? 'inherit' : '#fff5f5' }}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.rowNumber}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={row.isValid ? "Ready" : "Invalid"}
                                                                color={row.isValid ? "success" : "error"}
                                                                size="small"
                                                                sx={{ fontWeight: 800 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.data.staff_code || '-'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{row.data.staff_name || '-'}</TableCell>
                                                        <TableCell>{row.data.document_type || '-'}</TableCell>
                                                        <TableCell>{row.data.document_no || '-'}</TableCell>
                                                        <TableCell>{row.data.start_date || '-'}</TableCell>
                                                        <TableCell>{row.data.expiry_date || '-'}</TableCell>
                                                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {row.data.notes || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            <Stack spacing={0.5}>
                                                                {row.errors.map((err, idx) => (
                                                                    <Typography key={`err-${idx}`} variant="caption" sx={{ color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <ErrorIcon sx={{ fontSize: 14 }} /> {err}
                                                                    </Typography>
                                                                ))}
                                                                {row.warnings.map((warn, idx) => (
                                                                    <Typography key={`warn-${idx}`} variant="caption" sx={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <WarningIcon sx={{ fontSize: 14 }} /> {warn}
                                                                    </Typography>
                                                                ))}
                                                                {row.errors.length === 0 && row.warnings.length === 0 && (
                                                                    <Typography variant="caption" sx={{ color: '#047857', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <SuccessIcon sx={{ fontSize: 14 }} /> Pass
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* Sub-step 2: Success summary */}
                            {docStep === 2 && docResult && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Document Records Imported Successfully!</Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {docResult.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>Import Statistics</Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Vault records created:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{docResult.importedCount}</Typography>
                                        </Stack>
                                    </Box>
                                    <Button variant="outlined" onClick={resetDocWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                        Import More Records
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 7: Family Records Wizard */}
                    {tabVal === 6 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={famStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Family Records Sheet</StepLabel></Step>
                                <Step><StepLabel>Validation & Dry Run</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {/* Sub-step 0: File Upload */}
                            {famStep === 0 && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Family Records Import Instructions</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                                            Use this step to bulk import family member information for existing staff.
                                            You must provide the Employee ID / Code to identify each employee.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadFamTemplate}
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Download Family Template
                                        </Button>
                                    </Alert>

                                    {famLoadingVal ? (
                                        <Box sx={{ py: 4 }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Analyzing & validating spreadsheet rows...</Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: 5,
                                                p: 6,
                                                cursor: 'pointer',
                                                bgcolor: '#f8fafc',
                                                transition: 'all 0.2s',
                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Family Excel or CSV Sheet</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Supports .xlsx or .csv files</Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                Browse Local Files
                                            </Button>
                                            <input type="file" hidden accept=".xlsx,.csv" onChange={handleFamFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Sub-step 1: Review Validation Grid */}
                            {famStep === 1 && famValidation && (
                                <Box>
                                    {/* Stats Banner */}
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{famValidation.validRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{famValidation.invalidRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Rows with Errors (Will be Skipped)</Typography>
                                            </CardContent>
                                        </Card>
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetFamWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Back to Upload
                                        </Button>
                                        <Button
                                            startIcon={famImporting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                            onClick={handleExecuteFamImport}
                                            variant="contained"
                                            disabled={famImporting || famValidation.validRows === 0}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                                        >
                                            {famImporting ? "Executing Import..." : `Import ${famValidation.validRows} Valid Records`}
                                        </Button>
                                    </Box>

                                    {/* Table Preview Grid */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Excel Row</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Employee ID / Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Staff Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Member Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Relationship</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>D.O.B</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Working</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Studying</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Remarks</TableCell>
                                                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>Validation Errors &amp; Warnings</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {famValidation.records.map((row) => (
                                                    <TableRow key={row.rowNumber} sx={{ bgcolor: row.isValid ? 'inherit' : '#fff5f5' }}>
                                                        <TableCell sx={{ fontWeight: 800 }}>Row {row.rowNumber}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={row.isValid ? "Ready" : "Invalid"}
                                                                color={row.isValid ? "success" : "error"}
                                                                size="small"
                                                                sx={{ fontWeight: 800 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 700 }}>{row.data.staff_code || '-'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{row.data.staff_name || '-'}</TableCell>
                                                        <TableCell>{row.data.member_name || '-'}</TableCell>
                                                        <TableCell>{row.data.relationship || '-'}</TableCell>
                                                        <TableCell>{row.data.dob || '-'}</TableCell>
                                                        <TableCell>{row.data.is_working === 1 ? 'Yes' : 'No'}</TableCell>
                                                        <TableCell>{row.data.is_studying === 1 ? 'Yes' : 'No'}</TableCell>
                                                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {row.data.notes || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            <Stack spacing={0.5}>
                                                                {row.errors.map((err, idx) => (
                                                                    <Typography key={`err-${idx}`} variant="caption" sx={{ color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <ErrorIcon sx={{ fontSize: 14 }} /> {err}
                                                                    </Typography>
                                                                ))}
                                                                {row.warnings.map((warn, idx) => (
                                                                    <Typography key={`warn-${idx}`} variant="caption" sx={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <WarningIcon sx={{ fontSize: 14 }} /> {warn}
                                                                    </Typography>
                                                                ))}
                                                                {row.errors.length === 0 && row.warnings.length === 0 && (
                                                                    <Typography variant="caption" sx={{ color: '#047857', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <SuccessIcon sx={{ fontSize: 14 }} /> Pass
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* Sub-step 2: Success summary */}
                            {famStep === 2 && famResult && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Family Records Imported Successfully!</Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {famResult.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>Import Statistics</Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>Family records created:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e293b' }}>{famResult.importedCount}</Typography>
                                        </Stack>
                                    </Box>
                                    <Button variant="outlined" onClick={resetFamWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                        Import More Records
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 8: Historical Leave Records Wizard */}
                    {tabVal === 7 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={leaveStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Leave History Sheet</StepLabel></Step>
                                <Step><StepLabel>Validation &amp; Dry Run</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {leaveStep === 0 && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 680, mx: 'auto' }}>
                                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3, textAlign: 'left' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Historical Leave Import Rules</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                                            Import completed approved, rejected, or cancelled leave records. Approved rows update leave used totals.
                                            Cross-year and future records are rejected; split cross-year leave into separate rows. Opening balances are managed separately.
                                        </Typography>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadLeaveTemplate}
                                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>
                                                Download Leave History Template
                                            </Button>
                                        </Box>
                                    </Alert>
                                    {leaveLoadingVal ? (
                                        <Box sx={{ py: 4 }}>
                                            <CircularProgress sx={{ color: '#0f766e', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Checking employees, leave types, dates, overlaps, and duplicates...</Typography>
                                        </Box>
                                    ) : (
                                        <Box component="label" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: 5, p: 6, cursor: 'pointer', bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#0f766e' } }}>
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Leave History Excel or CSV</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Maximum 5,000 data rows per import</Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>Browse Local Files</Button>
                                            <input type="file" hidden accept=".xlsx,.csv" onChange={handleLeaveFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {leaveStep === 1 && leaveValidation && (
                                <Box>
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}><CardContent sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{leaveValidation.validRows}</Typography>
                                            <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
                                        </CardContent></Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}><CardContent sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{leaveValidation.invalidRows}</Typography>
                                            <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Invalid (Will Be Skipped)</Typography>
                                        </CardContent></Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}><CardContent sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#b45309' }}>{leaveValidation.warningRows || 0}</Typography>
                                            <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 700 }}>Rows with Warnings</Typography>
                                        </CardContent></Card>
                                    </Stack>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetLeaveWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>Back to Upload</Button>
                                        <Button startIcon={leaveImporting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />} onClick={handleExecuteLeaveImport}
                                            variant="contained" disabled={leaveImporting || leaveValidation.validRows === 0}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            {leaveImporting ? 'Executing Import...' : `Import ${leaveValidation.validRows} Valid Records`}
                                        </Button>
                                    </Box>
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                                        <Table stickyHeader size="small">
                                            <TableHead><TableRow>
                                                {['Excel Row', 'Status', 'Employee', 'Leave Type', 'Period', 'Days', 'Record Status', 'Source Ref.', 'Validation'].map(label =>
                                                    <TableCell key={label} sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>{label}</TableCell>)}
                                            </TableRow></TableHead>
                                            <TableBody>{leaveValidation.records.map(row => (
                                                <TableRow key={row.rowNumber} sx={{ bgcolor: row.isValid ? 'inherit' : '#fff5f5' }}>
                                                    <TableCell sx={{ fontWeight: 800 }}>Row {row.rowNumber}</TableCell>
                                                    <TableCell><Chip label={row.isValid ? 'Ready' : 'Invalid'} color={row.isValid ? 'success' : 'error'} size="small" sx={{ fontWeight: 800 }} /></TableCell>
                                                    <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{row.data.staff_code || '-'}</Typography><Typography variant="caption">{row.data.staff_name || ''}</Typography></TableCell>
                                                    <TableCell>{row.data.leave_type_code || '-'}{row.data.leave_type_name ? ` — ${row.data.leave_type_name}` : ''}</TableCell>
                                                    <TableCell>{row.data.start_date || '-'} → {row.data.end_date || '-'}</TableCell>
                                                    <TableCell>{Number.isFinite(row.data.total_days) ? row.data.total_days : '-'}</TableCell>
                                                    <TableCell sx={{ textTransform: 'capitalize' }}>{row.data.status || '-'}</TableCell>
                                                    <TableCell>{row.data.source_reference || '-'}</TableCell>
                                                    <TableCell><Stack spacing={0.5}>
                                                        {row.errors.map((error, index) => <Typography key={`e-${index}`} variant="caption" sx={{ color: '#b91c1c', display: 'flex', gap: 0.5 }}><ErrorIcon sx={{ fontSize: 14 }} />{error}</Typography>)}
                                                        {row.warnings.map((warning, index) => <Typography key={`w-${index}`} variant="caption" sx={{ color: '#d97706', display: 'flex', gap: 0.5 }}><WarningIcon sx={{ fontSize: 14 }} />{warning}</Typography>)}
                                                        {!row.errors.length && !row.warnings.length && <Typography variant="caption" sx={{ color: '#047857' }}>Pass</Typography>}
                                                    </Stack></TableCell>
                                                </TableRow>
                                            ))}</TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {leaveStep === 2 && leaveResult && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>Leave History Imported Successfully</Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>{leaveResult.message}</Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Records created</Typography><Typography variant="body2" sx={{ fontWeight: 800 }}>{leaveResult.importedCount}</Typography></Stack>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Approved days added to used balances</Typography><Typography variant="body2" sx={{ fontWeight: 800 }}>{leaveResult.approvedDays}</Typography></Stack>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Audit batch ID</Typography><Typography variant="body2" sx={{ fontWeight: 800 }}>{leaveResult.batchId}</Typography></Stack>
                                    </Box>
                                    <Button variant="outlined" onClick={resetLeaveWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>Import More Records</Button>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 9: Recurring Payroll Elements Wizard */}
                    {tabVal === 8 && (
                        <RecurringPayrollImportWizard />
                    )}

                    {/* Step 10: Qualifications & Education Wizard */}
                    {tabVal === 9 && (
                        <Box sx={{ p: 4 }}>
                            <Stepper activeStep={qualStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                                <Step><StepLabel>Upload Qualifications Sheet</StepLabel></Step>
                                <Step><StepLabel>Review & Validation</StepLabel></Step>
                                <Step><StepLabel>Import Completed</StepLabel></Step>
                            </Stepper>

                            {/* Step 0: Upload */}
                            {qualStep === 0 && (
                                <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                                    <Alert severity="info" sx={{ mb: 4, borderRadius: 3 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Flexible Qualification Importer</Typography>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                            Upload employee qualifications and credentials. Supports both PeopleHub Standard Excel Template (.xlsx) and Legacy HR Qualification Export (.csv, e.g. <code>TNL_Qualification List.csv</code>).
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadQualTemplate}
                                            size="small"
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Download Standard Template
                                        </Button>
                                    </Alert>

                                    {qualLoadingVal ? (
                                        <Box sx={{ py: 6, textAlign: 'center' }}>
                                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Analyzing & validating qualification records...</Typography>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed #cbd5e1',
                                                borderRadius: 5,
                                                p: 6,
                                                cursor: 'pointer',
                                                bgcolor: '#f8fafc',
                                                transition: 'all 0.2s',
                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: 'primary.main' }
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Qualification File (.xlsx or .csv)</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                                                Legacy CSV headers (Name, Level, Field, Major, Institution, Dates, etc.) are automatically parsed.
                                            </Typography>
                                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                                                Browse Local Files
                                            </Button>
                                            <input type="file" hidden accept=".xlsx,.csv" onChange={handleQualFileChange} />
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Step 1: Review Validation Grid */}
                            {qualStep === 1 && qualValidation && (
                                <Box>
                                    {/* Stats Banner */}
                                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{qualValidation.validRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{qualValidation.invalidRows}</Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Rows with Errors (Will be Skipped)</Typography>
                                            </CardContent>
                                        </Card>
                                        {qualValidation.warningRows > 0 && (
                                            <Card sx={{ flex: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#b45309' }}>{qualValidation.warningRows}</Typography>
                                                    <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 700 }}>Rows with Warnings</Typography>
                                                </CardContent>
                                            </Card>
                                        )}
                                    </Stack>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                        <Button startIcon={<BackIcon />} onClick={resetQualWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                            Back to Upload
                                        </Button>
                                        <Button
                                            startIcon={qualImporting ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                            disabled={qualImporting || qualValidation.validRows === 0}
                                            onClick={handleExecuteQualImport}
                                            variant="contained"
                                            sx={{
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                px: 4
                                            }}
                                        >
                                            {qualImporting ? 'Importing Qualifications...' : `Execute Import (${qualValidation.validRows} Records)`}
                                        </Button>
                                    </Box>

                                    {/* Table Preview */}
                                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 500 }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 800 }}>Row</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Employee Name / Code</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Category</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Level</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Major / Course</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Institution</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Timeline</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Overseas</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Result / CGPA</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>Errors / Warnings</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {qualValidation.records.map((r, idx) => (
                                                    <TableRow key={idx} sx={{ bgcolor: !r.isValid ? '#fef2f2' : r.warnings?.length ? '#fffbeb' : 'inherit' }}>
                                                        <TableCell>{r.rowIndex}</TableCell>
                                                        <TableCell>
                                                            {r.isValid ? (
                                                                <Chip label="Ready" size="small" color="success" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                                                            ) : (
                                                                <Chip label="Error" size="small" color="error" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.full_name || r.input_name || '-'}</Typography>
                                                            {r.employee_number && r.employee_number !== '-' && (
                                                                <Typography variant="caption" sx={{ color: '#64748b' }}>({r.employee_number})</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={r.category || 'ACADEMIC'} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.68rem' }} />
                                                        </TableCell>
                                                        <TableCell>{r.level || '-'}</TableCell>
                                                        <TableCell>{r.major_subject || '-'}</TableCell>
                                                        <TableCell>{r.institution_name || '-'}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                            {r.start_date || r.end_date ? `${r.start_date || ''} ~ ${r.end_date || ''}` : '-'}
                                                        </TableCell>
                                                        <TableCell>{r.is_overseas ? 'Yes' : 'No'}</TableCell>
                                                        <TableCell>{r.grade_result || (r.cgpa !== null && r.cgpa !== undefined ? `GPA ${r.cgpa}` : '-')}</TableCell>
                                                        <TableCell>
                                                            {r.errors?.map((err, ei) => (
                                                                <Typography key={ei} variant="caption" display="block" sx={{ color: '#b91c1c', fontWeight: 600 }}>• {err}</Typography>
                                                            ))}
                                                            {r.warnings?.map((w, wi) => (
                                                                <Typography key={wi} variant="caption" display="block" sx={{ color: '#b45309', fontWeight: 600 }}>• {w}</Typography>
                                                            ))}
                                                            {!r.errors?.length && !r.warnings?.length && '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* Step 2: Import Completed */}
                            {qualStep === 2 && qualResult && (
                                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 600, mx: 'auto' }}>
                                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 1 }}>
                                        Qualifications Imported Successfully
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                        {qualResult.message}
                                    </Typography>
                                    <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0', mb: 4 }}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2">Total Records Inserted</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{qualResult.count}</Typography>
                                        </Stack>
                                    </Box>
                                    <Button variant="outlined" onClick={resetQualWizard} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                                        Import Another File
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default StaffMigrationCenter;
